// =============================================================================
// src/data/analytics-client.ts — Cliente Hasura anon do Analytics (sl_*).
//
// Fino por cima do transporte compartilhado (hasura-graphql-post): monta as
// janelas de data (relógio INJETADO p/ testes determinísticos, padrão
// workspace-store) e devolve o dashboard com arrays sempre presentes.
// =============================================================================

import { postHasuraGraphql } from './hasura-graphql-post';
import {
  ANALYTICS_INSIGHT_EDITION_BODY_QUERY,
  ANALYTICS_INSIGHT_EDITION_LIST_QUERY,
  type AnalyticsCardEditionBodyGraphqlData,
  type AnalyticsCardEditionBodyRow,
  type AnalyticsCardEditionListGraphqlData,
  type AnalyticsCardEditionListRow,
} from './analytics-insights-edition-query';
import { buildAnalyticsDashboardQuery } from './analytics-dashboard-query';
import {
  ANALYTICS_ACTOR_FEED_QUERY,
  ANALYTICS_BLOCK_AUTHOR_MUTATION,
  ANALYTICS_INGESTION_QUERY,
  ANALYTICS_SCOPE_OVERRIDE_MUTATION,
  type AnalyticsActorFeedResult,
  type AnalyticsActorPlatformRow,
  type AnalyticsAuthorBlockResult,
  type AnalyticsIngestionGraphqlData,
  type AnalyticsRegionWindowRow,
  type AnalyticsScopeOverrideResult,
  type AnalyticsAuthorWindowRow,
  type AnalyticsGraphqlData,
  type AnalyticsIngestionFreshnessRow,
  type AnalyticsMentionsDailyRow,
  type AnalyticsMentionsDailyScopedRow,
  type AnalyticsMentionsFeedRow,
  type AnalyticsNewsSourceDailyRow,
  type AnalyticsWatchWindowRow,
} from './analytics-live-query';
import { analyticsPeriodWindowDays } from './analytics-overlay-scope';
import { ANALYTICS_DEFAULT_FEED_SCOPE, type AnalyticsFeedScope } from './analytics-scope-where';
import type { OmarAnalyticsPeriod } from '../features/analytics/analytics-types';

/** Buckets do feed já ordenados NO SQL (ordem do array = prioridade do merge). */
export interface AnalyticsFeedBuckets {
  readonly recent: readonly AnalyticsMentionsFeedRow[];
  readonly topEngagementPosts: readonly AnalyticsMentionsFeedRow[];
  readonly topLikedComments: readonly AnalyticsMentionsFeedRow[];
  readonly topViewedContent: readonly AnalyticsMentionsFeedRow[];
  readonly platformTops: readonly AnalyticsMentionsFeedRow[];
}

/** Janela das séries diárias (4 meses civis p/ a granularidade months do chart). */
const SERIES_WINDOW_DAYS = 120;
/** Janela do drill-down por ator (autores já vêm agregados em 30d pela view
 * v_author_window_30d, migration 012). Veículos e tops do dashboard seguem o
 * PERIODO da UI desde 2026-09-02 (analyticsPeriodWindowDays). */
const TOPS_WINDOW_DAYS = 30;

/** Recorte pedido ao dashboard: escopo (where no servidor) + periodo + reacoes. */
export interface AnalyticsDashboardFetchOptions {
  readonly scope: AnalyticsFeedScope;
  readonly period: OmarAnalyticsPeriod;
  readonly showReactions: boolean;
}

/** Nucleo do cenario AM, 30 dias, sem reacoes — o que os numeros SEMPRE mostraram. */
export const ANALYTICS_DEFAULT_DASHBOARD_OPTIONS: AnalyticsDashboardFetchOptions = {
  scope: ANALYTICS_DEFAULT_FEED_SCOPE,
  period: 'last30',
  showReactions: false,
};
const DEFAULT_TIMEOUT_MS = 8000;
/**
 * Limite PROPRIO da consulta do dashboard (2026-10-13). Ela traz ~1,3 MB (a serie
 * `v_mentions_daily_scoped` sozinha leva ~2,8 s no Hasura) e, disputando o boot com as
 * outras consultas, passava de 8 s no navegador: o abort derrubava o Analytics e o
 * Briefing inteiros para a base demonstrativa ("os dados recentes sumiram"). As demais
 * consultas seguem no default de 8 s.
 */
export const ANALYTICS_DASHBOARD_TIMEOUT_MS = 25000;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface AnalyticsLiveDashboard {
  readonly mentionsDaily: readonly AnalyticsMentionsDailyRow[];
  readonly feedBuckets: AnalyticsFeedBuckets;
  readonly authorWindow: readonly AnalyticsAuthorWindowRow[];
  readonly newsSourceDaily: readonly AnalyticsNewsSourceDailyRow[];
  readonly actorPlatformWindow: readonly AnalyticsActorPlatformRow[];
  /** 021: janelas 30d agregadas no SQL — recortes de tema/região da aba Análises. */
  readonly watchWindow: readonly AnalyticsWatchWindowRow[];
  readonly regionWindow: readonly AnalyticsRegionWindowRow[];
  /** 025: diaria por escopo (opcional p/ compat de fixtures; sem ela os KPIs
   * caem em mentionsDaily = cenario inteiro). */
  readonly mentionsDailyScoped?: readonly AnalyticsMentionsDailyScopedRow[];
  /** 025: frescor por plataforma. */
  readonly ingestionFreshness?: readonly AnalyticsIngestionFreshnessRow[];
  /** Recorte que gerou este dashboard (default = Nucleo/30d/sem reacoes). */
  readonly view?: AnalyticsDashboardFetchOptions;
  /** Relogio do fetch (ms) — base do "atualizado ha X" sem new Date() no overlay. */
  readonly fetchedAtMs?: number;
}

/** Pedido do drill-down do feed: personagem × tipo de relação × canais.
 * Arrays vazios significam "sem restrição" (_contains: [] casa tudo). */
export interface AnalyticsActorFeedRequest {
  readonly actorSlugs: readonly string[];
  readonly relations: readonly string[];
  readonly relationPairs: readonly string[];
  readonly rawPlatforms: readonly string[];
}

export interface AnalyticsLiveClientDeps {
  readonly endpoint: string;
  readonly clientId: string;
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
  /** Timeout so do dashboard (default ANALYTICS_DASHBOARD_TIMEOUT_MS). */
  readonly dashboardTimeoutMs?: number;
  /** Relógio injetado (DI p/ teste determinístico); default Date.now. */
  readonly nowMs?: () => number;
}

export interface AnalyticsAuthorBlockInput {
  readonly platform: string;
  readonly authorHandle: string;
  readonly reason: string;
}

export interface AnalyticsScopeOverrideInput {
  readonly contentId: string;
  readonly verdict: string;
  readonly reason: string;
}

export interface AnalyticsLiveClient {
  readonly fetchAnalyticsDashboard: (options?: AnalyticsDashboardFetchOptions) => Promise<AnalyticsLiveDashboard>;
  readonly fetchIngestionConfig: () => Promise<AnalyticsIngestionGraphqlData>;
  readonly fetchActorFeed: (feedRequest: AnalyticsActorFeedRequest) => Promise<AnalyticsActorFeedResult>;
  readonly blockAuthor: (blockInput: AnalyticsAuthorBlockInput) => Promise<void>;
  readonly overrideScope: (overrideInput: AnalyticsScopeOverrideInput) => Promise<void>;
  /** 022: lista das edicoes dos Cards Taticos (SEM corpo — navegacao). */
  readonly fetchInsightEditionList: () => Promise<readonly AnalyticsCardEditionListRow[]>;
  /** 022: corpo markdown de UMA edicao por data ('AAAA-MM-DD'). */
  readonly fetchInsightEditionBody: (editionDate: string) => Promise<AnalyticsCardEditionBodyRow | null>;
}

export function createAnalyticsLiveClient(deps: AnalyticsLiveClientDeps): AnalyticsLiveClient {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const dashboardTimeoutMs = deps.dashboardTimeoutMs ?? ANALYTICS_DASHBOARD_TIMEOUT_MS;
  const nowMs = deps.nowMs ?? Date.now;
  return {
    fetchAnalyticsDashboard: (options = ANALYTICS_DEFAULT_DASHBOARD_OPTIONS) =>
      fetchAnalyticsDashboard(deps.endpoint, deps.clientId, fetchImpl, dashboardTimeoutMs, nowMs, options),
    fetchIngestionConfig: () => fetchAnalyticsIngestionConfig(deps.endpoint, deps.clientId, fetchImpl, timeoutMs),
    fetchActorFeed: (feedRequest) => fetchAnalyticsActorFeed(deps.endpoint, deps.clientId, fetchImpl, timeoutMs, nowMs, feedRequest),
    blockAuthor: (blockInput) => blockAnalyticsAuthor(deps.endpoint, deps.clientId, fetchImpl, timeoutMs, blockInput),
    overrideScope: (overrideInput) => overrideAnalyticsScope(deps.endpoint, deps.clientId, fetchImpl, timeoutMs, overrideInput),
    fetchInsightEditionList: () => fetchAnalyticsInsightEditionList(deps.endpoint, deps.clientId, fetchImpl, timeoutMs),
    fetchInsightEditionBody: (editionDate) =>
      fetchAnalyticsInsightEditionBody(deps.endpoint, deps.clientId, fetchImpl, timeoutMs, editionDate),
  };
}

/** 022: refs das edicoes publicadas (ordenadas desc no SQL, corpo fora). */
async function fetchAnalyticsInsightEditionList(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
): Promise<readonly AnalyticsCardEditionListRow[]> {
  const data = await postHasuraGraphql<AnalyticsCardEditionListGraphqlData>({
    endpoint,
    clientId,
    query: ANALYTICS_INSIGHT_EDITION_LIST_QUERY,
    variables: { clientId },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
  return data.editionRefs ?? [];
}

/** 022: corpo markdown de uma edicao; `limit 1 + generated_at desc` na query. */
async function fetchAnalyticsInsightEditionBody(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
  editionDate: string,
): Promise<AnalyticsCardEditionBodyRow | null> {
  const data = await postHasuraGraphql<AnalyticsCardEditionBodyGraphqlData>({
    endpoint,
    clientId,
    query: ANALYTICS_INSIGHT_EDITION_BODY_QUERY,
    variables: { clientId, editionDate },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
  return data.editionBodies?.[0] ?? null;
}

/** Espelho somente-leitura da configuração de ingestão (021, aba Configurações). */
async function fetchAnalyticsIngestionConfig(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
): Promise<AnalyticsIngestionGraphqlData> {
  const data = await postHasuraGraphql<AnalyticsIngestionGraphqlData>({
    endpoint,
    clientId,
    query: ANALYTICS_INGESTION_QUERY,
    variables: { clientId },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
  return {
    watchQueries: data.watchQueries ?? [],
    profiles: data.profiles ?? [],
    cadences: data.cadences ?? [],
    scopeTerms: data.scopeTerms ?? [],
    outletScopes: data.outletScopes ?? [],
    blockedAuthors: data.blockedAuthors ?? [],
  };
}

/** Conteúdo REAL do personagem (recentes + top engajamento) na janela de 30d. */
async function fetchAnalyticsActorFeed(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
  nowMs: () => number,
  feedRequest: AnalyticsActorFeedRequest,
): Promise<AnalyticsActorFeedResult> {
  const data = await postHasuraGraphql<AnalyticsActorFeedResult>({
    endpoint,
    clientId,
    query: ANALYTICS_ACTOR_FEED_QUERY,
    variables: {
      clientId,
      actorSlugs: feedRequest.actorSlugs,
      relations: feedRequest.relations,
      relationPairs: feedRequest.relationPairs,
      rawPlatforms: feedRequest.rawPlatforms,
      topsSinceTs: new Date(nowMs() - TOPS_WINDOW_DAYS * DAY_MS).toISOString(),
    },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
  return { actorRecent: data.actorRecent ?? [], actorTop: data.actorTop ?? [] };
}

/** Grava o autor na blocklist do cliente (as views de serving já o excluem). */
async function blockAnalyticsAuthor(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
  blockInput: AnalyticsAuthorBlockInput,
): Promise<void> {
  await postHasuraGraphql<AnalyticsAuthorBlockResult>({
    endpoint,
    clientId,
    query: ANALYTICS_BLOCK_AUTHOR_MUTATION,
    variables: { platform: blockInput.platform, authorHandle: blockInput.authorHandle, reason: blockInput.reason },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
}

/** Marca um CONTEUDO fora (ou dentro) do escopo Amazonas — curadoria humana que o
 * content_relevance_scoring sempre respeita (regra 0). */
async function overrideAnalyticsScope(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
  overrideInput: AnalyticsScopeOverrideInput,
): Promise<void> {
  await postHasuraGraphql<AnalyticsScopeOverrideResult>({
    endpoint,
    clientId,
    query: ANALYTICS_SCOPE_OVERRIDE_MUTATION,
    variables: { contentId: overrideInput.contentId, verdict: overrideInput.verdict, reason: overrideInput.reason },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
}

/** Data ISO (YYYY-MM-DD) de `windowDays` atrás — variável `date` do Hasura. */
function analyticsIsoDateDaysAgo(nowMs: () => number, windowDays: number): string {
  return new Date(nowMs() - windowDays * DAY_MS).toISOString().slice(0, 10);
}

async function fetchAnalyticsDashboard(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
  nowMs: () => number,
  options: AnalyticsDashboardFetchOptions,
): Promise<AnalyticsLiveDashboard> {
  const periodDays = analyticsPeriodWindowDays(options.period);
  const fetchedAtMs = nowMs();
  const data = await postHasuraGraphql<AnalyticsGraphqlData>({
    endpoint,
    clientId,
    query: buildAnalyticsDashboardQuery({ scope: options.scope, showReactions: options.showReactions }),
    variables: {
      clientId,
      sinceDate: analyticsIsoDateDaysAgo(nowMs, SERIES_WINDOW_DAYS),
      authorSinceDate: analyticsIsoDateDaysAgo(nowMs, periodDays),
      topsSinceTs: new Date(fetchedAtMs - periodDays * DAY_MS).toISOString(),
    },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Analytics',
  });
  return {
    view: options,
    fetchedAtMs,
    mentionsDailyScoped: data.mentionsDailyScoped ?? [],
    ingestionFreshness: data.ingestionFreshness ?? [],
    mentionsDaily: data.sl_serving_v_mentions_daily ?? [],
    feedBuckets: {
      recent: data.recentMentions ?? [],
      topEngagementPosts: data.topEngagementPosts ?? [],
      topLikedComments: data.topLikedComments ?? [],
      topViewedContent: data.topViewedContent ?? [],
      platformTops: [
        ...(data.tiktokTopContent ?? []),
        ...(data.facebookTopContent ?? []),
        ...(data.xTopContent ?? []),
      ],
    },
    authorWindow: data.sl_serving_v_author_window_30d ?? [],
    newsSourceDaily: data.sl_serving_v_news_source_daily ?? [],
    actorPlatformWindow: data.actorPlatformWindow ?? [],
    watchWindow: data.watchWindow ?? [],
    regionWindow: data.regionWindow ?? [],
  };
}
